// src/components/NotificationSettings.jsx
import React, { useState, useEffect } from 'react';
import { requestNotificationPermission, getFCMToken, saveFCMToken } from '../utils/notificationManager';
import { auth, db, doc, updateDoc, getDoc } from '../firebase';
import { useTranslation } from 'react-i18next';

const NotificationSettings = () => {
  const { t } = useTranslation();
  const [permission, setPermission] = useState('default');
  const [fcmToken, setFcmToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);

  useEffect(() => {
    checkNotificationStatus();
  }, []);

  const checkNotificationStatus = async () => {
    if ('Notification' in window) {
      setPermission(Notification.permission);
    }
    
    // Load current FCM token and notification status from user profile
    if (auth.currentUser) {
      try {
        const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          if (userData.fcmToken) {
            setFcmToken(userData.fcmToken);
            setPermission('granted'); // Set permission to granted when FCM token exists
          }
          // Check if notifications are enabled (notificationsEnabled field)
          setNotificationsEnabled(userData.notificationsEnabled !== false);
        }
      } catch (error) {
        console.error('Error loading FCM token:', error);
      }
    }
  };

  const enableNotifications = async () => {
    setLoading(true);
    try {
      const hasPermission = await requestNotificationPermission();
      if (hasPermission) {
        // Get FCM token (works for both web and native platforms now)
        const token = await getFCMToken();
        if (token) {
          if (auth.currentUser) {
            await saveFCMToken(auth.currentUser.uid, token);
            // Also enable notifications by setting notificationsEnabled to true
            const { updateUserProfileWithBackup } = await import('../utils/clientBackupWrapper');
            await updateUserProfileWithBackup(auth.currentUser.uid, {
              notificationsEnabled: true
            }, 'notification_update', {
              field: 'notifications_enabled',
              enabled: true,
              reason: 'fcm_token_saved'
            });
          } else {
            localStorage.setItem('pendingFCMToken', token);
          }
          setFcmToken(token);
          setPermission('granted');
          setNotificationsEnabled(true);
        } else {
          // Token retrieval failed
          setPermission('denied');
          console.error('Failed to get FCM token');
        }
      } else {
        setPermission('denied');
      }
    } catch (error) {
      console.error('Error enabling notifications:', error);
    }
    setLoading(false);
  };

  const disableNotifications = async () => {
    setLoading(true);
    try {
      if (auth.currentUser) {
        // Use backup wrapper for notification disable
        const { updateUserProfileWithBackup } = await import('../utils/clientBackupWrapper');
        await updateUserProfileWithBackup(auth.currentUser.uid, {
          fcmToken: null,
          lastTokenUpdate: null,
          notificationsEnabled: false
        }, 'notification_update', {
          field: 'notifications_disabled',
          disabled: true,
          reason: 'user_request'
        });
        setFcmToken(null);
        setPermission('default'); // Reset to default so user can re-enable
        setNotificationsEnabled(false);
        
        // Clear stored FCM token from localStorage
        localStorage.removeItem('fcmToken');
        localStorage.removeItem('pendingFCMToken');
        
        console.log('✅ Notifications disabled successfully');
      }
    } catch (error) {
      console.error('Error disabling notifications:', error);
    }
    setLoading(false);
  };

  const toggleNotifications = async () => {
    if (notificationsEnabled) {
      await disableNotifications();
    } else {
      await enableNotifications();
    }
  };

  return (
    <div
      className="mute-toggle-in-modal"
      onClick={toggleNotifications}
      style={{ opacity: loading ? 0.6 : 1, pointerEvents: loading ? 'none' : 'auto' }}
    >
      {notificationsEnabled ? '🔔 Notifications ON' : '🔕 Notifications OFF'}
    </div>
  );
};

export default NotificationSettings;
